const REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes

const userTokens = new Map();
const refreshTimers = new Map();

self.addEventListener('install', () => {
  self.skipWaiting();
});

self.addEventListener('activate', (event) => {
  event.waitUntil(self.clients.claim());
});

self.addEventListener('message', async (event) => {
  const { type, payload } = event.data;
  switch (type) {
    case 'START_REFRESH':
      startTokenRefresh(payload);
      break;

    case 'UPDATE_TOKEN':
      updateToken(payload);
      break;

    case 'STOP_REFRESH':
      stopTokenRefresh(payload.sessionId);
      break;

    case 'RECHECK':
      userTokens.forEach((_, id) => scheduleRefresh(id));
      break;
  }
});

/**
 * Initializes token refresh monitoring for a session
 * Prevents duplicate timers for the same session
 */
function startTokenRefresh({ sessionId, token, expiresAt }) {
  const existing = userTokens.get(sessionId);
  if (existing && existing.expiresAt >= expiresAt) return;
  userTokens.set(sessionId, { sessionId, token, expiresAt });
  scheduleRefresh(sessionId);
}

/**
 * Updates token information after successful refresh
 * Reschedule the next refresh based on new expiration
 */
function updateToken({ sessionId, token, expiresAt }) {
  const existing = userTokens.get(sessionId);
  if (existing) {
    userTokens.set(sessionId, { sessionId, token, expiresAt });
    scheduleRefresh(sessionId);
  }
}

/**
 * Schedules token refresh for a specific session
 * Clears any existing timer before setting a new one
 */
function scheduleRefresh(sessionId) {
  const existingTimer = refreshTimers.get(sessionId);
  if (existingTimer) clearTimeout(existingTimer);

  const tokenData = userTokens.get(sessionId);

  if (!tokenData) return;
  if (tokenData.expiresAt - Date.now() <= 0) {
    triggerRefresh(sessionId);
    return;
  }

  const timeUntilRefresh = tokenData.expiresAt - Date.now() - REFRESH_BUFFER;
  if (timeUntilRefresh <= 0) triggerRefresh(sessionId);
  else {
    const timer = setTimeout(() => triggerRefresh(sessionId), timeUntilRefresh);
    refreshTimers.set(sessionId, timer);
  }
}

/**
 * Triggers token refresh by broadcasting to all clients.

 * includeUncontrolled: true ensures the message reaches tabs that loaded before
 * the Service Worker took control (e.g. first load, hard refresh).
 * type: 'window' filters out non-page clients (shared workers, etc.).

 * Each tab's TokenRefreshManager looks up refreshCallbacks.get(sessionId) and
 * either executes the refresh (if it owns that session) or silently ignores it.
 */
async function triggerRefresh(sessionId) {
  const tokenData = userTokens.get(sessionId);
  if (!tokenData) return;

  const clients = await self.clients.matchAll({
    includeUncontrolled: true,
    type: 'window',
  });

  clients.forEach((client) => {
    client.postMessage({
      type: 'REFRESH_TOKEN_REQUEST',
      payload: { sessionId },
    });
  });
}

/**
 * Stops token refresh monitoring for a session
 * Cleans up timers and stored token data
 */
function stopTokenRefresh(sessionId) {
  const timer = refreshTimers.get(sessionId);
  if (timer) {
    clearTimeout(timer);
    refreshTimers.delete(sessionId);
  }
  userTokens.delete(sessionId);
}

/* ── Web Push ──────────────────────────────────────────────────────────── */

const NOTIFICATION_ICON = '/icons/icon-maskable-192x192.png';
const NOTIFICATION_BADGE = '/icons/icon-badge-96x96.png';
// Windows the AUTOMY shell does not run in: app runtimes, public forms, portals, login.
const NON_SHELL_PATH_RE = /^\/(apps\/[^/]+\/?$|external(\/|$)|s\/|p\/|confirmAccount|user\/)/i;

/** Same-origin path only, so a push payload can never navigate the user off AUTOMY. */
function toSafeAppPath(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl) return '/';
  try {
    const url = new URL(rawUrl, self.location.origin);
    if (url.origin !== self.location.origin) return '/';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_) {
    return '/';
  }
}

function readPushPayload(event) {
  if (!event.data) return {};
  try {
    const parsed = event.data.json();
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    try {
      return { body: event.data.text() };
    } catch (__) {
      return {};
    }
  }
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function readTimestamp(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

async function updateAppBadge(unreadCount) {
  if (typeof unreadCount !== 'number' || !Number.isFinite(unreadCount)) return;
  if (!self.navigator || typeof self.navigator.setAppBadge !== 'function') return;
  try {
    if (unreadCount > 0) await self.navigator.setAppBadge(unreadCount);
    else await self.navigator.clearAppBadge();
  } catch (_) {
    /* badge is cosmetic */
  }
}

async function showPushNotification(event) {
  const payload = readPushPayload(event);
  const details = asObject(payload.data);
  const title = typeof payload.title === 'string' && payload.title ? payload.title : 'AUTOMY';
  // The unified payload puts the id in `tag` and the route in `data.url`.
  // Older payloads kept both at the top level.
  const notificationId = details.notificationId
    ? String(details.notificationId)
    : payload.notificationId
    ? String(payload.notificationId)
    : typeof payload.tag === 'string' && payload.tag
    ? payload.tag
    : null;
  const rawUrl = typeof details.url === 'string' ? details.url : payload.url;
  const tag = typeof payload.tag === 'string' && payload.tag ? payload.tag : notificationId;
  const timestamp = readTimestamp(payload.timestamp);
  const options = {
    body: typeof payload.body === 'string' ? payload.body : '',
    icon: NOTIFICATION_ICON,
    badge: NOTIFICATION_BADGE,
    data: {
      url: toSafeAppPath(rawUrl),
      notificationId,
      type: typeof details.type === 'string' ? details.type : null,
      eventType: typeof details.eventType === 'string' ? details.eventType : null,
      entityId:
        details.entityId != null && details.entityId !== '' ? String(details.entityId) : null,
    },
  };
  if (timestamp) options.timestamp = timestamp;
  // renotify without a tag throws. Same tag replaces the previous alert;
  // renotify stays off unless the payload explicitly asks to buzz again.
  if (tag) {
    options.tag = tag;
    options.renotify = payload.renotify === true;
  }

  // Show before the badge. setAppBadge can stall once the app process is gone,
  // and a stalled push handler never reaches the screen.
  await self.registration.showNotification(title, options);
  await updateAppBadge(payload.unreadCount);
}

function isShellWindow(client) {
  try {
    const { origin, pathname } = new URL(client.url);
    return origin === self.location.origin && !NON_SHELL_PATH_RE.test(pathname);
  } catch (_) {
    return false;
  }
}

async function openFromNotification(data) {
  const path = toSafeAppPath(data && data.url);
  const notificationId = data && data.notificationId ? String(data.notificationId) : null;

  const windows = await self.clients.matchAll({ type: 'window', includeUncontrolled: true });
  const shellWindow = windows.find(isShellWindow);
  if (shellWindow) {
    try {
      const focused = await shellWindow.focus();
      (focused || shellWindow).postMessage({
        type: 'PUSH_NOTIFICATION_CLICK',
        payload: { url: path, notificationId },
      });
      return;
    } catch (_) {
      /* focus can be refused; fall back to a new window */
    }
  }

  const target = new URL(path, self.location.origin);
  if (notificationId) target.searchParams.set('pushNotificationId', notificationId);
  await self.clients.openWindow(`${target.pathname}${target.search}${target.hash}`);
}

self.addEventListener('push', (event) => {
  event.waitUntil(showPushNotification(event));
});

self.addEventListener('notificationclick', (event) => {
  event.notification.close();
  event.waitUntil(openFromNotification(event.notification.data));
});
